import React from "react";
import { BonusOfferService } from "../../services/BonusOfferService";
import { getContainer } from "../../ioc/IOCSetup";
import { BonusConfigService } from "../../services/BonusConfigService";
import { Form, FormListener } from "../form/Form";
import { AwardBonusOfferRequest, BonusConfig, BonusStatus, BonusStatusAlert, AdminSecuritySettings } from "../../http/protocol";
import { Collapsible } from "../Collapsible";
import { SelectInput } from "../form/SelectInput";
import { FormSubmitError } from "../form/FormFeedback";
import { Submit } from "../form/Submit";
import { FormInput } from "../form/FormInput";
import { Validators } from "../form/Validators";
import { t } from "../../i18n";
import { CurrencyRateService } from "../../services/CurrencyRateService";
import UserRewardVerification, { RequestFeedback, VerificationType } from "./UserRewardVerification";
import { CurrencyEuroValue } from "../account/CurrencyEuroValue";
import { LargeAmountConfirmationDialog } from "../dialog/LargeAmountConfirmationDialog";
import { SupervisorValidationModal } from "../dialog/SupervisorValidationModal";
import { LargeAmountConfirmationHandler } from "../util/largeAmountConfirmation";

interface State {
  form: AwardBonusOfferRequest;
  validationMap: any;
  configs: BonusConfig[];
  selectedBonus: BonusConfig;
  verificationModal: boolean;
  currencyToEuroRate: number | undefined;
  requestFeedback: boolean;
  showConfirmationDialog: boolean;
  euroAmountForConfirmation?: number;
  largeAmountThreshold?: number;
  supervisorValidationMessage?: string;
}

interface Props {
  userId: number;
  currencyEuroValueEnabled?: boolean;
}

export class UserBonusComponent extends React.Component<Props, State> implements FormListener<AwardBonusOfferRequest> {
  bonusAwardService: BonusOfferService;
  bonusConfigService: BonusConfigService;
  currencyRateService: CurrencyRateService;
  private confirmationHandler: LargeAmountConfirmationHandler;
  private userCancelled: boolean = false;

  constructor(props: Props) {
    super(props);
    this.toggleVerificationModal = this.toggleVerificationModal.bind(this);
    this.requestFeedback = this.requestFeedback.bind(this);
    this.bonusAwardService = getContainer().getBonusOfferService();
    this.bonusConfigService = getContainer().getBonusConfigService();
    this.currencyRateService = getContainer().getCurrencyRateService();
    this.confirmationHandler = new LargeAmountConfirmationHandler();
    this.state = {
      form: { expiryInDays: 0 },
      validationMap: {},
      configs: [],
      selectedBonus: {},
      verificationModal: false,
      currencyToEuroRate: undefined,
      requestFeedback: false,
      showConfirmationDialog: false,
    };
  }

  componentDidMount() {
    this.confirmationHandler.loadSecuritySettings();
    this.bonusConfigService.list({ limit: 500, offset: 0, status: BonusStatus.ENABLED }).then((resp) => {
      if (resp.items && resp.items.length > 0) {
        let bonuses: BonusConfig[] = [];
        resp.items.map((bonus) => {
          if (bonus.status === "ENABLED" && (bonus.bonusStatusAlert === BonusStatusAlert.GREEN || bonus.bonusStatusAlert === BonusStatusAlert.YELLOW)) {
            bonuses.push(bonus);
          }
        });
        const bonusConfigId = (bonuses && bonuses[0] && bonuses[0].id) || null;
        const initForm = this.state.form;
        if (bonusConfigId != null) {
          initForm.bonusConfigId = bonusConfigId;
        }
        this.setState({ configs: bonuses, form: initForm, selectedBonus: bonuses[0] });
      }
    });
  }

  componentWillUnmount() {
    this.confirmationHandler.reset();
  }

  private calculateDisplayAmount(amount?: number, awardAmountFraction?: number, maxInputAmount?: number): number {
    let displayAmount = amount || 0;
    if (maxInputAmount && displayAmount > maxInputAmount) {
      displayAmount = maxInputAmount;
    }
    if (displayAmount && awardAmountFraction) {
      displayAmount = displayAmount * awardAmountFraction;
    }
    return displayAmount;
  }

  formDidUpdate(formModel: AwardBonusOfferRequest, validationMap?: any) {
    formModel.userId = this.props.userId;
    const bonusConfig: BonusConfig = this.state.configs.find((bonus) => bonus.id === formModel.bonusConfigId) || {};
    this.setState({ form: formModel, validationMap: validationMap, selectedBonus: bonusConfig });
  }

  async checkThresholdAndProceed(): Promise<any> {
    const { form, selectedBonus } = this.state;

    if (!selectedBonus?.monetaryConfig) {
      return;
    }

    if (!form.claimableOnNextDeposit) {
      const minInputAmount = selectedBonus.monetaryConfig.minInputAmount ?? 0;
      const inputAmount = form.amount ?? 0;
      if (inputAmount < minInputAmount) {
        return Promise.reject({
          message: `Input amount must be at least ${minInputAmount} ${selectedBonus.monetaryConfig.currencyCode || ""}`.trim(),
        });
      }
    }

    this.userCancelled = false;

    if (form.claimableOnNextDeposit) {
      return this.proceedToVerification(selectedBonus.monetaryConfig.currencyCode);
    }

    const currency = selectedBonus.monetaryConfig.currencyCode;
    const displayAmount = this.calculateDisplayAmount(
      form.amount,
      selectedBonus.monetaryConfig.awardAmountFraction,
      selectedBonus.monetaryConfig.maxInputAmount,
    );

    if (!displayAmount || !currency) {
      return this.proceedToVerification(currency);
    }

    try {
      const result = await this.confirmationHandler.checkThreshold(displayAmount, currency);

      if (result.requiresConfirmation) {
        this.setState({
          euroAmountForConfirmation: result.euroAmount,
          showConfirmationDialog: true,
          largeAmountThreshold: this.confirmationHandler.getThreshold(),
        });

        try {
          await this.confirmationHandler.waitForConfirmation();
        } catch (error: any) {
          if (error?.message === "USER_CANCELLED") {
            return;
          }
          throw error;
        }
      }

      return this.proceedToVerification(currency);
    } catch (error: any) {
      console.error("Error checking threshold:", error);
      this.confirmationHandler.resetSubmissionFlag();
      throw error;
    }
  }

  private proceedToVerification(currency?: string): Promise<void> {
    if (!currency) {
      console.error("No currency provided for verification");
      return Promise.reject({ message: "Currency is required for verification" });
    }

    const code = this.confirmationHandler.trimmedCurrency(currency);
    return this.currencyRateService
      .getCryptoAndFiatToEuroExchangeRates(code)
      .then((rate) => {
        this.setState({ currencyToEuroRate: rate, verificationModal: true });
      })
      .catch((error) => {
        console.error(`Failed to get exchange rate for ${code}`, error);
        throw { message: `Failed to fetch exchange rate for ${code}. Please try again.` };
      });
  }

  onSubmit() {
    return this.checkThresholdAndProceed();
  }

  handleConfirmLargeAmount = () => {
    this.setState({
      showConfirmationDialog: false,
      euroAmountForConfirmation: undefined,
    });
    this.userCancelled = false;
    this.confirmationHandler.confirmLargeAmount();
  };

  handleCancelLargeAmount = () => {
    this.setState({
      showConfirmationDialog: false,
      euroAmountForConfirmation: undefined,
    });
    this.userCancelled = true;
    this.confirmationHandler.cancelLargeAmount();
  };

  toggleVerificationModal() {
    this.setState({ verificationModal: false });
    this.confirmationHandler.resetSubmissionFlag();
  }

  requestFeedback(type: string) {
    if (type == "open") {
      this.setState({ requestFeedback: true });
    }
    if (type == "close") {
      this.setState({ requestFeedback: false });
      this.setState({ form: { ...this.state.form, amount: undefined }, selectedBonus: this.state.selectedBonus });
    }
  }

  toDisplayValue(a: BonusConfig) {
    if (a.name) {
      return a.name.toString();
    } else {
      return "";
    }
  }

  getVerificationModal() {
    return (
      <UserRewardVerification
        type={VerificationType.bonus_reward}
        selectedBonus={this.state.selectedBonus}
        bonusRewardRequest={this.state.form}
        toggleVerificationModal={this.toggleVerificationModal}
        isVerificationModalOpen={this.state.verificationModal}
        euroExchangeRate={this.state.currencyToEuroRate}
        requestFeedback={this.requestFeedback}
        onSupervisorRequired={(message) => this.setState({ supervisorValidationMessage: message })}
      />
    );
  }

  getSuccessModal() {
    if (this.state.requestFeedback) {
      return (
        <RequestFeedback
          type={VerificationType.bonus_reward}
          requestFeedback={this.requestFeedback}
          isOpen={this.state.requestFeedback}
        />
      );
    }
  }

  getBonusDetails() {
    if (this.state.selectedBonus && this.state.selectedBonus.monetaryConfig) {
      const conf = this.state.selectedBonus.monetaryConfig;
      return (
        <tbody>
          <tr>
            <td>{t(`dataTable.header.currency`)}</td>
            <td>{conf.currencyCode}</td>
          </tr>
          <tr>
            <td>{t(`dataTable.header.awardAmountFactor`)}</td>
            <td>{conf.awardAmountFraction}</td>
            <span className="help-block">{t(`helpText.awardAmountFraction`)}</span>
          </tr>
          <tr>
            <td>{t(`dataTable.header.turnoverAmountFactor`)}</td>
            <td>{conf.turnoverRequirementFactor}</td>
            <span className="help-block">{t(`helpText.turnoverRequirementFactor`)}</span>
          </tr>
          <tr>
            <td>Min Input Amount</td>
            <td>{conf.minInputAmount ?? 0}</td>
          </tr>
        </tbody>
      );
    } else {
      return <tbody />;
    }
  }

  getForm() {
    let amount =
      this.calculateDisplayAmount(
        this.state.form.amount,
        this.state.selectedBonus?.monetaryConfig?.awardAmountFraction,
        this.state.selectedBonus?.monetaryConfig?.maxInputAmount,
      ) || undefined;

    const readyToAwardOnNextDeposit = this.state.form.claimableOnNextDeposit && this.state.form.expiryInDays && this.state.form.expiryInDays > 0;
    const minInputAmount = this.state.selectedBonus?.monetaryConfig?.minInputAmount ?? 0;
    const readyToAwardInstantly =
      !this.state.form.claimableOnNextDeposit && typeof this.state.form.amount !== "undefined" && this.state.form.amount >= minInputAmount;
    const isReadyToAward = readyToAwardOnNextDeposit || readyToAwardInstantly;

    const buttonText = this.state.form.claimableOnNextDeposit ? t(`button.award`) : t(`button.award`) + " " + amount;

    return (
      <Form
        className="manual-adjustment-form container-fluid"
        formListener={this}
        validationMap={this.state.validationMap}
        modelObject={this.state.form}
      >
        <div className="row">
          <div className="col-sm-12">
            <SelectInput
              className="form-control"
              model="bonusConfigId"
              values={this.state.configs}
              displayValue={this.toDisplayValue}
              optionModelValue={(a: BonusConfig) => {
                return a.id;
              }}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <label style={{ paddingTop: "10px" }}>{t(`label.bonusDetails`)}</label>
            <table className={"table table-striped"}>{this.getBonusDetails()}</table>
          </div>
        </div>
        <div className="row">
          {!this.state.form.claimableOnNextDeposit && (
            <div className="col-sm-6">
              <label>{t(`label.inputAmount`)}</label>
              <FormInput
                className="form-control"
                model="amount"
                type="float"
                validators={[Validators.number()]}
              />
              {this.props.currencyEuroValueEnabled && (
                <CurrencyEuroValue
                  sourceCurrency={this.state.selectedBonus?.monetaryConfig?.currencyCode}
                  sourceAmount={this.state.form.amount}
                />
              )}
              <span className="help-block">{t(`helpText.inputAmount`)}</span>
            </div>
          )}
          {this.state.form.claimableOnNextDeposit && (
            <div className="col-sm-6">
              <label>Expiry in days</label>
              <FormInput
                className="form-control"
                model="expiryInDays"
                type="number"
              />
            </div>
          )}
          <div className="col-sm-6">
            <div className="custom-control custom-checkbox">
              <FormInput
                className="custom-control-input"
                model="claimableOnNextDeposit"
                id={"claimableOnNextDeposit"}
                type="checkbox"
              />
              <label
                htmlFor={"claimableOnNextDeposit"}
                className={"custom-control-label"}
              >
                Claimable on next deposit
              </label>
              <span className="help-block">If checked, it will be claimable by user on next deposit otherwise will be claimable instantly</span>
            </div>
          </div>
          <div className="col-sm-6">
            <label></label>
            <FormSubmitError />
            <Submit
              disabled={!isReadyToAward}
              className="btn btn-primary btn-block"
            >
              {buttonText}
            </Submit>
          </div>
        </div>
      </Form>
    );
  }

  render() {
    const { showConfirmationDialog, form, selectedBonus, euroAmountForConfirmation } = this.state;

    const displayAmount = this.calculateDisplayAmount(
      form.amount,
      selectedBonus?.monetaryConfig?.awardAmountFraction,
      selectedBonus?.monetaryConfig?.maxInputAmount,
    );

    return (
      <>
        <Collapsible
          title={t(`title.manualBonusAward`)}
          collapsed={true}
        >
          {this.getForm()}
          {this.getVerificationModal()}
          {this.getSuccessModal()}
        </Collapsible>

        <SupervisorValidationModal
          show={!!this.state.supervisorValidationMessage}
          message={this.state.supervisorValidationMessage || ""}
          onClose={() => this.setState({ supervisorValidationMessage: undefined })}
        />

        {showConfirmationDialog && selectedBonus?.monetaryConfig?.currencyCode && (
          <LargeAmountConfirmationDialog
            show={showConfirmationDialog}
            originalAmount={displayAmount}
            originalCurrency={selectedBonus.monetaryConfig.currencyCode}
            euroAmount={euroAmountForConfirmation || 0}
            onConfirm={this.handleConfirmLargeAmount}
            onCancel={this.handleCancelLargeAmount}
          />
        )}
      </>
    );
  }
}
